<template>
  <LexonTooltipComponent
    v-if="isTruncated"
    :message="generateTooltipMessage(props.item)"
    position="top"
    class="tooltip-activator caso1"
    isWidthAuto
  >
    <template #activator-element>
      <div v-if="Array.isArray(props.item.data)">
        <PhoneListComponent :label="props.item.label" :data="props.item.data" :index="index" @setRef="onSetRef" />
      </div>
      <div v-else class="row-info">
        <span class="info-item-label">{{ `${props.item.label}:` }}</span>
        <div :ref="(el) => onSetRef(el, index)" class="info-item-data-large">{{ item.data }}</div>
      </div>
    </template>
  </LexonTooltipComponent>
</template>

<script setup lang="ts">
import { defineProps, defineEmits } from 'vue'
import LexonTooltipComponent from '@/components/forms/fields/LexonTooltip/LexonTooltipComponent.vue'
import PhoneListComponent from '@/components/widgets/ExpedientWidgets/ExpedientSummaryWidget/PhoneListComponent.vue'
import { Icons } from '@/icons/icons'
import { sanitizeHtml } from '@/helpers/html'

interface DataItem {
  name: string
  phone?: string
}

interface ItemData {
  label: string
  data: string | DataItem[]
}

// PROPS
const props = defineProps<{
  item: ItemData
  index: number
  isTruncated: boolean
}>()

// DATA
const icon = {
  phone: Icons.CALL
}

// EMITS
const emit = defineEmits(['setRef'])

// METHODS
const onSetRef = (el: any, index: number) => {
  if (el) {
    emit('setRef', el, index)
  }
}

const generateTooltipMessage = (item: ItemData) => {
  if (Array.isArray(item.data)) {
    const listItems = item.data.map(createPhoneListItem).join('')
    return `<ul class="tooltip-phone-list">${listItems}</ul>`
  }
  return ''
}

const createPhoneListItem = (subItem: DataItem) => {
  const sanitizedName = sanitizeHtml(subItem.name)
  const sanitizedPhone = subItem.phone ? sanitizeHtml(subItem.phone) : ''

  return `<li class="phone-list-item">
    <span class="tooltip-phone-name">${sanitizedName}</span>
    ${createPhoneDisplay(sanitizedPhone)}
  </li>`
}

const createPhoneDisplay = (phoneNumber: string) => {
  if (!phoneNumber) return ''

  return `<span class="tooltip-phone-container">
    <a href="tel:${phoneNumber}" class="tooltip-phone-link">
      <i class="${icon.phone} tooltip-phone-icon"></i>
      <span class="tooltip-phone-number">${phoneNumber}</span>
    </a>
  </span>`
}
</script>

<style lang="scss" scoped>
.info-item-label {
  @include milano-bold-16;
  color: $neutral-grey-800;
  padding: 0 $spacing-xxs;
  margin-right: $spacing-xs;
  white-space: nowrap;
  flex-shrink: 0;
}

.info-item-data-large {
  @include milano-regular-14;
  color: $neutral-grey-800;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: none;
  width: 100%;
}

.row-info {
  display: flex;
  width: 100%;
  align-items: center;
  padding: $spacing-xxs 0;
  background-color: transparent;
}
</style>

<style lang="scss">
.tooltip-phone-list {
  display: flex;
  list-style: none;
  padding-left: 0 !important;

  .tooltip-phone {
    color: $main-400;
    font-weight: bold;
  }

  li:not(:last-of-type)::after {
    content: '·';
    margin-right: 6px;
  }

  .phone-list-item {
    display: flex;
    align-items: center;

    .tooltip-phone-name {
      white-space: nowrap;
      max-width: 100%;
    }
  }

  .tooltip-phone-container {
    display: inline-flex;
    font-size: 10px;
    align-items: center;
    justify-content: center;
    color: $neutral-grey-800;
    border-radius: $spacing-xxs;
    border: 0.5px solid $main-400;
    padding: 4px;
    height: 20px;
    margin: 0 6px;
  }

  .tooltip-phone-link {
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    color: inherit;
  }

  .tooltip-phone-icon {
    margin-right: 4px;
    font-size: 12px;
  }

  .tooltip-phone-number {
    font-size: 12px;
    line-height: 16px;
    padding-top: 1px;
  }
}
</style>
